import { useState, useEffect, useMemo, useCallback } from 'react'
import ReactWebChat, { createStore } from 'botframework-webchat'
import PropTypes from 'prop-types'
import useSound from 'use-sound'
import { useTranslation } from 'react-i18next'
import { useHeader } from 'providers/AreasProvider'
import { FakeText, IconButton, Typography, useToast } from '@totalsoft/rocket-ui'
import Refresh from '@mui/icons-material/Refresh'
import { Grid2 as Grid } from '@mui/material'

import { getOidcAccessToken } from 'utils/auth/oidcFunctions'

import { forceScrollDown, createConfig } from './functions'
import InnerHeader from './InnerHeader'
import {
  DIRECT_LINE_POST_ACTIVITY_FULFILLED,
  WEB_CHAT_SET_SEND_BOX_ATTACHMENTS,
  LANGUAGE_CHANGED,
  WEBCHAT_UPLOAD,
  DIRECT_LINE_INCOMING_ACTIVITY
} from '../../../constants/webchat/activityNames'
import { BOT } from '../../../constants/webchat/roles'
import { EVENT } from '../../../constants/webchat/activityTypes'
import autoscrollActivityTypes from '../../../constants/webchat/autoscrollActivityTypes'
import { moveAttachmentsWebchatToAttachments, sendFilesToChatbot } from './upload'
import SendLocationButton from './custom/SendLocationButton'

const Chat = ({
  isAnonymous,
  userId,
  username,
  urlConfig,
  handleConnectToDirectline,
  handleReconnectToDirectline,
  styleSet,
  chatbotName,
  headerFollowupText,
  botAvatar,
  refreshTokenTimeout = 600000,
  displayInnerHeader = false,
  hideUploadButton = false,
  hideNewConversationButton = false,
  customLocalization,
  emojiSet = false,
  soundUrl = '',
  forceAutoscrollOnNewMessage = false,
  isDevelopment = false,
  headerChatVersion = '0.0.0'
}) => {
  const { baseUrl, tokenUrl, uploadUrl, logsUrl } = urlConfig
  const { i18n, t } = useTranslation()
  const locale = i18n.language
  const [, setHeader] = useHeader()
  const showToast = useToast()

  const [directLine, setDirectLine] = useState()
  const fullTokenUrl = baseUrl + tokenUrl
  const fullUploadUrl = baseUrl + uploadUrl
  const fullLogsUrl = baseUrl + logsUrl

  const [playMessageSound] = useSound(soundUrl)

  const store = useMemo(
    () =>
      createStore({}, ({ dispatch }) => next => async action => {
        const { type } = action
        if (forceAutoscrollOnNewMessage && autoscrollActivityTypes.includes(type)) {
          forceScrollDown(document)
        }
        console.log(action)
        switch (type) {
          case DIRECT_LINE_INCOMING_ACTIVITY: {
            const {
              activity: {
                from: { role },
                name,
                type,
                value
              }
            } = action.payload
            if (role === BOT && type === EVENT && name === LANGUAGE_CHANGED) {
              i18n.changeLanguage(value)
            }
            return next(action)
          }
          case WEB_CHAT_SET_SEND_BOX_ATTACHMENTS:
            // Clear file input to allow re-uploading the same file
            setTimeout(() => {
              const fileInputs = document.querySelectorAll('.webchat__upload-button--file-input')
              fileInputs.forEach(input => {
                input.value = ''
              })
            }, 0)

            if (uploadUrl && action.payload.attachments && action.payload.attachments.length > 0) {
              let latestAccessToken = null
              if (!isAnonymous) {
                const { accessToken } = getOidcAccessToken()
                latestAccessToken = accessToken
              }
              try {
                await sendFilesToChatbot(dispatch, action, fullUploadUrl, fullLogsUrl, latestAccessToken, showToast, t)
              } catch (err) {
                console.error(err)
              }
              return
            } else {
              return next(action)
            }
          case DIRECT_LINE_POST_ACTIVITY_FULFILLED: {
            playMessageSound()
            return next(action)
          }
          default:
            return next(action)
        }
      }),
    [playMessageSound, forceAutoscrollOnNewMessage, uploadUrl, fullUploadUrl, fullLogsUrl, i18n, directLine, showToast] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const activityMiddleware = useMemo(
    () =>
      () =>
      next =>
      (...setupArgs) => {
        const [first] = setupArgs
        if (first) {
          let { activity } = first
          // Trick webchat into displaying the attachments as a message from custom upload.
          if (uploadUrl && activity.name === WEBCHAT_UPLOAD) {
            moveAttachmentsWebchatToAttachments(activity)
          }
        }
        return next(...setupArgs)
      },
    [uploadUrl]
  )

  const sendLocationToolbar =
    () =>
    next =>
    (...args) => {
      const RestOfToolbar = next(...args)
      //eslint-disable-next-line react/display-name
      return () => (
        <>
          <SendLocationButton />
          <RestOfToolbar />
        </>
      )
    }

  // Initial connection to DirectLine
  useEffect(() => {
    const options = {
      userId,
      username,
      fullTokenUrl,
      getNewConversation: false,
      setDirectLine
    }
    handleConnectToDirectline(options)
  }, [fullTokenUrl, userId, username, setDirectLine, handleConnectToDirectline])

  // Refresh token
  useEffect(() => {
    const interval = setInterval(() => {
      const options = {
        userId,
        username,
        fullTokenUrl,
        getNewConversation: false,
        directLine,
        setDirectLine
      }
      handleReconnectToDirectline(options)
    }, refreshTokenTimeout)
    return () => clearInterval(interval)
  }, [fullTokenUrl, userId, username, directLine, setDirectLine, handleReconnectToDirectline, refreshTokenTimeout])

  const handleGetNewConversation = useCallback(() => {
    const options = {
      userId,
      username,
      fullTokenUrl,
      getNewConversation: true,
      setDirectLine
    }
    handleConnectToDirectline(options)
  }, [fullTokenUrl, userId, username, setDirectLine, handleConnectToDirectline])

  // Header with chatbot name, followup text and new conversation button
  useEffect(() => {
    const name = chatbotName[locale] || chatbotName.default
    const subtext = headerFollowupText[locale] || headerFollowupText.default
    setHeader(
      <Grid container spacing={1} alignItems='center' justifyContent='space-between'>
        {!hideNewConversationButton && (
          <Grid>
            <IconButton
              id='chatbot_newConversationBtn'
              color='transparent'
              onClick={handleGetNewConversation}
              tooltip={t('Chatbot.NewConversation')}
            >
              <Refresh />
            </IconButton>
          </Grid>
        )}
        <Grid>
          <Typography style={{ fontWeight: 'bold' }}>{name}</Typography>
        </Grid>
        <Grid>
          <Typography>{subtext}</Typography>
        </Grid>
      </Grid>
    )
  }, [setHeader, t, hideNewConversationButton, handleGetNewConversation, chatbotName, locale, headerFollowupText])

  const restOfProps = useMemo(
    () => createConfig(styleSet, botAvatar, hideUploadButton, customLocalization, emojiSet),
    [botAvatar, customLocalization, emojiSet, hideUploadButton, styleSet]
  )

  return (
    <>
      {displayInnerHeader && (
        <InnerHeader
          displayChatbotName={chatbotName}
          headerFollowupText={headerFollowupText}
          locale={locale}
          isDevelopment={isDevelopment}
          headerChatVersion={headerChatVersion}
        />
      )}
      <div className='chatbot-body'>
        {directLine ? (
          <ReactWebChat
            directLine={directLine}
            store={store}
            userID={userId}
            username={username}
            locale={locale}
            activityMiddleware={activityMiddleware}
            sendBoxToolbarMiddleware={[sendLocationToolbar]}
            {...restOfProps}
          />
        ) : (
          <FakeText lines={10} />
        )}
      </div>
      <div className='chatbot-version'>
        <span hidden={!isDevelopment}>{headerChatVersion}</span>
      </div>
    </>
  )
}

Chat.propTypes = {
  isAnonymous: PropTypes.bool.isRequired,
  userId: PropTypes.string,
  username: PropTypes.string,
  urlConfig: PropTypes.object.isRequired,
  styleSet: PropTypes.object,
  displayInnerHeader: PropTypes.bool,
  displayUserName: PropTypes.string,
  chatbotName: PropTypes.object.isRequired,
  refreshTokenTimeout: PropTypes.number,
  headerFollowupText: PropTypes.object.isRequired,
  botAvatar: PropTypes.object,
  customLocalization: PropTypes.object,
  hideUploadButton: PropTypes.bool,
  hideNewConversationButton: PropTypes.bool,
  emojiSet: PropTypes.bool || PropTypes.object,
  soundUrl: PropTypes.string,
  forceAutoscrollOnNewMessage: PropTypes.bool,
  isDevelopment: PropTypes.bool,
  headerChatVersion: PropTypes.string,
  handleConnectToDirectline: PropTypes.func.isRequired,
  handleReconnectToDirectline: PropTypes.func.isRequired
}

export default Chat
